import { z } from "zod";

export const COHORT_COLORS = [
  "red",
  "green",
  "blue",
  "yellow",
  "purple",
  "orange",
  "pink",
  "brown",
  "gray",
  "black",
  "white",
] as const;

export const COHORT_FREQUENCIES = ["monthly", "biweekly", "weekly"] as const;

export const cohortColorsSchema = z
  .string()
  .transform(color => color.toLowerCase().trim())
  .pipe(z.enum(COHORT_COLORS));

export const frequencySchema = z
  .string()
  .transform(frequency => frequency.toLowerCase().trim())
  .pipe(z.enum(COHORT_FREQUENCIES));

export const scheduleSchema = z.object({
  enabled: z.boolean(),
  frequency: frequencySchema,
});

export const notificationSchema = z.object({
  notifications: z.boolean(),
});

export const notificationScheduleSchema = notificationSchema.extend({
  schedule: scheduleSchema,
});

export const adtSchema = z.object({
  enabled: z.boolean(),
});

export const monitoringSchema = z
  .object({
    adt: adtSchema,
    hie: scheduleSchema,
    pharmacy: notificationScheduleSchema,
    laboratory: notificationSchema,
  })
  .strict();

const overridesSchema = z.record(z.string(), z.boolean()).optional();

export const settingsSchema = z
  .object({
    monitoring: monitoringSchema,
    overrides: overridesSchema,
  })
  .strict();

export const cohortSchema = z.object({
  id: z.string(),
  cxId: z.string(),
  name: z.string(),
  color: cohortColorsSchema,
  description: z.string().optional(),
  settings: settingsSchema,
  size: z.number().optional(),
});

export type CohortColor = z.infer<typeof cohortColorsSchema>;
export type CohortFrequency = z.infer<typeof frequencySchema>;
export type Frequency = (typeof COHORT_FREQUENCIES)[number];
export type Schedule = z.infer<typeof scheduleSchema>;
export type NotificationSchedule = z.infer<typeof notificationScheduleSchema>;
export type Monitoring = z.infer<typeof monitoringSchema>;
export type Settings = z.infer<typeof settingsSchema>;
export type Cohort = z.infer<typeof cohortSchema>;

const cohortCreateSchema = cohortSchema.deepPartial().extend({ name: z.string() });
export type CohortCreate = z.infer<typeof cohortCreateSchema>;

const cohortUpdateSchema = cohortSchema.deepPartial();
export type CohortUpdate = z.infer<typeof cohortUpdateSchema>;

// Form schemas use strings for Select components
export const booleanStringSchema = z.enum(["true", "false"]);
export const scheduleFormSchema = z.object({
  enabled: booleanStringSchema,
  frequency: z.enum(COHORT_FREQUENCIES),
});
export const cohortBaseFormSchema = cohortSchema.pick({ name: true, description: true }).extend({
  name: z.string().min(1, "Name is required"),
  color: z.enum(COHORT_COLORS),
  overrides: z.string(),
});
